import { useEffect } from 'react';
import { useRouteMatch } from 'react-router-dom';
import useLocalStorage from 'react-use/lib/useLocalStorage.js';
import useVodProductsQuery from 'hooks/useVodProductsQuery.js';
import useVideosQuery from '../../../Videos/hooks/useVideosQuery.js';
import useDistributionContainersQuery from 'pages/Distribution/hooks/useDistributionContainersQuery.js';
import useDistributionPagesQuery from 'pages/Distributions/hooks/useDistributionPagesQuery.js';
import { exampleProductUrl } from '../TrialOnboardingModal/hooks/useCreateExampleProduct.js';
import useVodThemes from 'pages/Theming/hooks/useVodThemes.js';
import useVodActionsQuery from 'hooks/useVodActionsQuery.js';
import useProductDataOutsideRoutes from 'hooks/useProductDataOutsideRoutes.js';
import { ON_DEMAND_PRODUCT } from 'constants/products.js';
import { useFeatureContext } from 'contexts/FeatureContext.jsx';
import useProducts from 'pages/CatalogProducts/hooks/useProducts.js';

export const STEPS_KEYS = {
  INTEGRATION: 'INTEGRATION',
  UPLOAD_VIDEO: 'UPLOAD_VIDEO',
  DISPLAY: 'DISPLAY',
  ADD_PRODUCT: 'ADD_PRODUCT',
};

export const EXTRA_STEPS_KEYS = {
  THEMING: 'THEMING',
  ACTION_CARDS: 'ACTION_CARDS',
  STATS: 'STATS',
  TRACK_SALES: 'TRACK_SALES',
};

const mapCatalogProductsToVodProducts = (products) => {
  if (!products) return [];
  return products.map(p => ({
    id: p.id,
    sku: p.id,
    title: p.title,
    sourceUrl: p.link,
    data: {
      brand: p.brand,
      thumbnail: p.image_link,
      price: {
        original: p.price,
        amount: p.sale_price,
        currency: p.currency,
      },
    },
  }));
};

export const useGetStartedVodStatus = () => {
  const { selectedProduct } = useProductDataOutsideRoutes();
  const { features: { productCatalogProductsVODEnabled } } = useFeatureContext();
  const isVod = selectedProduct === ON_DEMAND_PRODUCT;
  const { data: { data: videos } = {} } = useVideosQuery({}, { enabled: isVod });
  const { data: { data: vodProducts } = {} } = useVodProductsQuery({}, {
    enabled: isVod && !productCatalogProductsVODEnabled,
  });
  const { data: { items: catalogProducts } = {} } = useProducts({
    enabled: isVod && productCatalogProductsVODEnabled,
  });
  const { data: { data: pages } = {} } = useDistributionContainersQuery({}, { enabled: isVod });
  const { data: { data: integrations } = {} } = useDistributionPagesQuery({ controlledBy: 'site' }, { enabled: isVod });
  const { data: themes } = useVodThemes({}, { enabled: isVod });
  const { data: { data: actionsInOrg } = {} } = useVodActionsQuery({}, { enabled: isVod });
  const [vodStatsOpened, setVodStatsOpened] = useLocalStorage(`stats_opened_${selectedProduct}`, false);

  const matchEmbedStatsRoute = useRouteMatch('/:orgId/on-demand/embed-stats');

  useEffect(() => {
    if (!matchEmbedStatsRoute) return;

    setVodStatsOpened(true);
  }, [matchEmbedStatsRoute, setVodStatsOpened]);

  const videosWithoutExamples = videos?.filter(
    ({ fieldValues }) => !fieldValues?.map(({ string }) => string).includes('example'),
  );

  const products = productCatalogProductsVODEnabled ? mapCatalogProductsToVodProducts(catalogProducts) : vodProducts;

  return {
    [STEPS_KEYS.UPLOAD_VIDEO]: videosWithoutExamples?.length > 0,
    [STEPS_KEYS.DISPLAY]: pages?.length > 0,
    [STEPS_KEYS.ADD_PRODUCT]: products?.filter((product) => product.sourceUrl !== exampleProductUrl).length > 0,
    [STEPS_KEYS.INTEGRATION]: integrations?.length > 0,
    [EXTRA_STEPS_KEYS.THEMING]: themes?.length > 0,
    [EXTRA_STEPS_KEYS.ACTION_CARDS]: actionsInOrg?.length > 0,
    [EXTRA_STEPS_KEYS.STATS]: vodStatsOpened,
  };
};
